import { useEffect, useId, useState } from 'react';
import { t } from '@app/i18n';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  LoadingButton,
  useToast,
} from '@zarya/ui';
import {
  useDeleteChatEntry,
  useChatEntry,
  useTestChatEntry,
  useUpdateChatEntry,
} from './ChatEntry.api';
import { ChatEntryForm } from './ChatEntryForm';
import {
  formFromState,
  testNotices,
  testRequestOf,
  updateOf,
  type ChatEntryForm as ChatEntryFormValues,
  type ChatEntryState,
  type ChatEntryTestResult,
} from './ChatEntryModel';
import { chatEntrySchema } from './useFormValidation';
import s from './Chat.module.css';

export interface ChatEntryDrawerProps {
  open: boolean;
  /** The identity whose entry the drawer shows. */
  identityId: string;
  agent: string;
  /** Only the admin changes the entry (D5): the approver reads it and runs the test. */
  canEdit: boolean;
  onClose: () => void;
}

/**
 * The drawer «Entrada do chat» (aim-5wc, prototype v2 §3.2–§3.3): how the Chat delivers the question to this agent.
 *
 * The form opens on what the server says — the configured entry, or the Segura default — and a stored secret opens
 * EMPTY: the mask never becomes a value (`SiteModel.ts` records the trap). «Testar» tests what the drawer shows, saved
 * or not, and sends no token and no question.
 */
export function ChatEntryDrawer({
  open,
  identityId,
  agent,
  canEdit,
  onClose,
}: ChatEntryDrawerProps) {
  const formId = useId();
  const toast = useToast();
  const state = useChatEntry(open ? identityId : null);
  const update = useUpdateChatEntry();
  const restore = useDeleteChatEntry();
  const test = useTestChatEntry();
  const [tested, setTested] = useState<{
    result: ChatEntryTestResult;
    protocol: ChatEntryFormValues['protocol'];
    port: string;
  } | null>(null);

  const { control, handleSubmit, formState, reset, getValues, watch } =
    useForm<ChatEntryFormValues>({
      mode: 'onChange',
      resolver: yupResolver(chatEntrySchema()),
    });

  // The form is reset on what the server answered, every time it answers: opening the drawer for another agent, or
  // reading the state again after a save, must never keep the values of the previous one. And it shows only AFTER the
  // reset: a form that mounts before the answer freezes its values empty (`UsersPage`, 10/09).
  const [shown, setShown] = useState<ChatEntryState | null>(null);
  useEffect(() => {
    if (!state.data) return;
    reset(formFromState(state.data));
    setShown(state.data);
  }, [state.data, reset]);
  const ready = state.data !== undefined && shown === state.data;

  useEffect(() => {
    if (!open) {
      setTested(null);
      test.reset();
      update.reset();
      restore.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const values = watch();
  const pending = update.isPending || restore.isPending;

  function save(form: ChatEntryFormValues) {
    if (!state.data) return;
    update.mutate(
      { identityId, body: updateOf(form, state.data) },
      {
        onSuccess: () => {
          toast.mostrar(t('chat.entry.saved'), { tone: 'sucesso' });
          onClose();
        },
      },
    );
  }

  function backToDefault() {
    restore.mutate(identityId, {
      onSuccess: () => {
        toast.mostrar(t('chat.entry.restored'), { tone: 'sucesso' });
        onClose();
      },
    });
  }

  function runTest() {
    const form = getValues();
    setTested(null);
    test.mutate(
      { identityId, body: testRequestOf(form) },
      {
        onSuccess: (result) =>
          setTested({ result, protocol: form.protocol, port: form.port.trim() }),
      },
    );
  }

  const footer = canEdit ? (
    <DrawerFooter
      start={
        state.data?.custom ? (
          <Button
            variant="out"
            onClick={backToDefault}
            disabled={pending}
            data-testid="chat-entry-restore"
          >
            {t('chat.entry.restore')}
          </Button>
        ) : null
      }
    >
      <Button variant="out" onClick={onClose} disabled={pending}>
        {t('chat.entry.cancel')}
      </Button>
      <LoadingButton
        type="submit"
        form={formId}
        loading={update.isPending}
        disabled={!ready || !formState.isDirty || !formState.isValid}
        data-testid="chat-entry-save"
      >
        {t('chat.entry.save')}
      </LoadingButton>
    </DrawerFooter>
  ) : (
    <DrawerFooter>
      <Button variant="out" onClick={onClose}>
        {t('chat.entry.drawer.close')}
      </Button>
    </DrawerFooter>
  );

  return (
    <Drawer
      open={open}
      title={t('chat.entry.drawer.title', { agent })}
      subtitle={t('chat.entry.drawer.subtitle')}
      dirty={canEdit && formState.isDirty && !pending}
      onClose={onClose}
      footer={footer}
    >
      {state.isPending ? (
        <p role="status" data-testid="chat-entry-loading">
          {t('chat.entry.drawer.loading')}
        </p>
      ) : null}

      {state.isError ? (
        <ErrorBanner
          error={state.error}
          title={t('chat.entry.drawer.loadError')}
          onRetry={() => void state.refetch()}
          retryLabel={t('chat.entry.drawer.retry')}
        />
      ) : null}

      {ready ? (
        <>
          {!canEdit ? (
            <Alert tone="info" role="note">
              {t('chat.entry.drawer.readOnly')}
            </Alert>
          ) : null}

          {update.error || restore.error ? (
            <ErrorBanner
              error={update.error ?? restore.error}
              escrita
              title={t('chat.entry.saveError')}
              hideLink
            />
          ) : null}

          <ChatEntryForm
            control={control}
            formId={formId}
            readOnly={!canEdit}
            values={values}
            onSubmit={handleSubmit(save)}
          />

          <section
            className={s.entryTest}
            aria-label={t('chat.entry.test')}
            data-testid="chat-entry-test"
          >
            <LoadingButton
              variant="out"
              loading={test.isPending}
              onClick={runTest}
              disabled={!formState.isValid}
            >
              {t('chat.entry.test')}
            </LoadingButton>
            {test.error ? (
              <ErrorBanner error={test.error} title={t('chat.entry.test.error')} hideLink />
            ) : null}
            {tested ? (
              <ul className={s.entryTestResult} data-testid="chat-entry-test-result">
                {testNotices(tested.result, tested.protocol, tested.port).map((notice) => (
                  <li key={notice.kind} data-kind={notice.kind} data-tone={notice.tone}>
                    {t(`chat.entry.test.${notice.kind}`, notice.params)}
                  </li>
                ))}
              </ul>
            ) : null}
            <p className={s.entryNote}>{t('chat.entry.test.note')}</p>
          </section>
        </>
      ) : null}
    </Drawer>
  );
}
